/**
 * Legacy Unity `/account/*` URLs → canonical `/settings/*` targets.
 *
 * These paths predate the move of account administration under `/settings`.
 * The Ember app forwarded them via per-route redirect pods; Hydra now owns the
 * forward natively (see `account.$.tsx`), so the embedded app never has to boot
 * just to bounce the URL.
 *
 * `resolveAccountRedirect` returns `null` for paths we intentionally do NOT own
 * yet — those fall through to the embedded Journeys app, which still handles
 * them. Deferred on purpose:
 *   - `subscription/new`, `api_credentials/:id/confirm_delete`, `receipts/:id`
 *     → target routes that don't exist in the account-level settings tree
 *       (reproducing them would just redirect to a 404).
 *   - `workspaces/:id/edit`, `workspaces/:id/confirm_delete`
 *     → workspace-scoped (`workspace-settings.*`), not an account `/settings/*`
 *       path; needs the workspace id resolved to a canonical Hydra URL.
 *   - the `cancelled` account states → run billing initialization before
 *     redirecting; handled separately.
 */

// Exact splat → target. The splat is the portion after `/account/`.
const STATIC_ACCOUNT_REDIRECTS: Record<string, string> = {
  '': '/settings/account',
  edit: '/settings/account/edit',
  subscription: '/settings/subscription',
  security: '/settings/personal/security',
  'security/new': '/settings/personal/security/new',
  'security/backup_codes': '/settings/personal/security/backup_codes',
  api_credentials: '/settings/api_credentials',
  // `/account/api_credentials/new` historically dropped the `/new` segment.
  'api_credentials/new': '/settings/api_credentials',
  receipts: '/settings/receipts',
  team: '/settings/team',
  workspaces: '/settings/workspaces',
  'workspaces/new': '/settings/workspaces/new',
};

// `/account/team/:id/passwords/edit` → `/settings/team/set_password`
// (the user id is intentionally dropped, matching the Ember redirect).
const TEAM_PASSWORD_EDIT = /^team\/[^/]+\/passwords\/edit$/;

export function resolveAccountRedirect(splat: string): string | null {
  const normalized = splat.replace(/\/+$/, '');

  if (Object.prototype.hasOwnProperty.call(STATIC_ACCOUNT_REDIRECTS, normalized)) {
    return STATIC_ACCOUNT_REDIRECTS[normalized];
  }

  if (TEAM_PASSWORD_EDIT.test(normalized)) {
    return '/settings/team/set_password';
  }

  return null;
}
